import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import {
  ACTIVE_PREQUALIFICATION_WINDOW_GRAPHQL,
  ATTEND_SUPPLIER_CHECKLIST_ITEM_GRAPHQL,
  ENROLL_PREQUALIFICATION_GRAPHQL,
  GET_PREQUALIFICATION_BY_CURRENT_WINDOW_GRAPHQL,
  GET_PREQUALIFICATION_PDF_GRAPHQL,
  GET_SUPPLIER_PREQUALIFICATION_BY_UUID_GRAPHQL,
  SUBMIT_SUPPLIER_PREQUALIFICATION_GRAPHQL,
} from '../graphql/supplier-registration.graphql';
import { GENERATE_CONTROL_NUMBER } from '@modules/registration/omc-registration/graphql/omc-registration.graphql';
import {
  PrequalificationWindowNoticePdfResponse,
  PrequalificationWindowResponse,
  EnrollToPrequalificationResponse,
} from '../responses/prequalification-window.response';
import {
  PrequalificationWindowActions,
  PrequalificationWindowNoticePdfActions,
  EnrollPrequalificationActions,
  PrequalificationForCurrentWindowActions,
  PrequalificationByUuidActions,
  SupplierPaymentActions,
  AttendSupplierCheckListItemActions,
  SubmitSupplierPrequalificationActions,
} from './supplier-registration.action';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  GetSupplierPrequalificationByUuidResponse,
  GetSupplierPrequalificationResponse,
} from '../responses/current-window-prequalification.respomse';
import { GenerateControlNumberResponse } from '@modules/registration/omc-registration/responses/generate,control-number.response';
import { AttendSupplierCheckListItemResponse } from '../responses/attend-checklist-item.response';
import { SubmitSupplierPrequalificationResponse } from '../responses/submit-supplier-prequalification.response';
import { VIEW_SUPPLIER_APPLICATION } from '@modules/external-services/supplier-management/graphql/supplier-applications.graphql';

@Injectable({
  providedIn: 'root',
})
export class SupplierRegistrationEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
    private router: Router,
  ) {}

  loadPrequalificationWindow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrequalificationWindowActions.request),
      switchMap(() =>
        this.graphQlService
          .query<PrequalificationWindowResponse>({
            query: ACTIVE_PREQUALIFICATION_WINDOW_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.getActivePrequalificationWindow;
              if (!payload?.status) {
                return PrequalificationWindowActions.failure({
                  error: {
                    message: payload?.message || 'Failed to load prequalification window',
                    data: payload,
                  },
                });
              }

              return PrequalificationWindowActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                PrequalificationWindowActions.failure({
                  error: {
                    message: error?.message || 'Failed to load prequalification window',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  getPrequalificationWindowNoticePdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrequalificationWindowNoticePdfActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<PrequalificationWindowNoticePdfResponse>({
            query: GET_PREQUALIFICATION_PDF_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.getPrequalificationWidowNoticePdf;
              if (!payload?.status) {
                return PrequalificationWindowNoticePdfActions.failure({
                  error: {
                    message:
                      payload?.message || 'Failed to load prequalification window notice PDF',
                    data: payload,
                  },
                });
              }

              return PrequalificationWindowNoticePdfActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                PrequalificationWindowNoticePdfActions.failure({
                  error: {
                    message: error?.message || 'Failed to load prequalification window notice PDF',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  enrollPrequalification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollPrequalificationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<EnrollToPrequalificationResponse>({
            mutation: ENROLL_PREQUALIFICATION_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              qualificationUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.enrollToPrequalification;
              if (!payload?.status) {
                return EnrollPrequalificationActions.failure({
                  error: {
                    message: payload?.message || 'Failed to enroll to prequalification',
                    data: payload,
                  },
                });
              }

              return EnrollPrequalificationActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                EnrollPrequalificationActions.failure({
                  error: {
                    message: error?.message || 'Failed to enroll to prequalification',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  prequalificationCurrentWindow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrequalificationForCurrentWindowActions.request),
      switchMap(() =>
        this.graphQlService
          .mutate<GetSupplierPrequalificationResponse>({
            mutation: GET_PREQUALIFICATION_BY_CURRENT_WINDOW_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.getSupplierPrequalificationByCurrentWindow;
              if (!payload?.status) {
                return PrequalificationForCurrentWindowActions.failure({
                  error: {
                    message:
                      payload?.message || 'Failed to load prequalification for current window',
                    data: payload,
                  },
                });
              }

              return PrequalificationForCurrentWindowActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                PrequalificationForCurrentWindowActions.failure({
                  error: {
                    message: error?.message || 'Failed to load prequalification for current window',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  prequalificationByUuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrequalificationByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetSupplierPrequalificationByUuidResponse>({
            query: VIEW_SUPPLIER_APPLICATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.getSupplierPreQualificationByUuid;
              if (!payload?.status) {
                return PrequalificationByUuidActions.failure({
                  error: {
                    message: payload?.message || 'Failed to load supplier prequalification',
                    data: payload,
                  },
                });
              }

              return PrequalificationByUuidActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                PrequalificationByUuidActions.failure({
                  error: {
                    message: error?.message || 'Failed to load supplier prequalification',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  generateControlNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierPaymentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<GenerateControlNumberResponse>({
            mutation: GENERATE_CONTROL_NUMBER,
            apolloNamespace: ApolloNamespace.billing,
            variables: { billUuid: request.billingUuid },
          })
          .pipe(
            map((response) => {
              const payload = response.data.generateControlNumber;
              if (!payload?.status) {
                return SupplierPaymentActions.failure({
                  error: {
                    message: payload?.message || 'Failed to generate control number',
                    data: payload,
                  },
                });
              }

              return SupplierPaymentActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                SupplierPaymentActions.failure({
                  error: {
                    message: error?.message || 'Failed to generate control number',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  attendSupplierCheckListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttendSupplierCheckListItemActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<AttendSupplierCheckListItemResponse>({
            mutation: ATTEND_SUPPLIER_CHECKLIST_ITEM_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              attendCheckListItemDtoInput: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.attendSupplierCheckListItem;
              if (!payload?.status) {
                return AttendSupplierCheckListItemActions.failure({
                  error: {
                    message: payload?.message || 'Failed to attend supplier checklist item',
                    data: payload,
                  },
                });
              }

              return AttendSupplierCheckListItemActions.success({
                response: payload.data,
              });
            }),
            catchError((error) =>
              of(
                AttendSupplierCheckListItemActions.failure({
                  error: {
                    message: error?.message || 'Failed to attend supplier checklist item',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitSupplierPrequalification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmitSupplierPrequalificationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitSupplierPrequalificationResponse>({
            mutation: SUBMIT_SUPPLIER_PREQUALIFICATION_GRAPHQL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              preQualificationUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.submitSupplierPrequalification;
              if (!payload?.status) {
                return SubmitSupplierPrequalificationActions.failure({
                  error: {
                    message: payload?.message || 'Failed to submit supplier prequalification',
                    data: payload,
                  },
                });
              }

              return SubmitSupplierPrequalificationActions.success({
                response: payload.data ?? null,
              });
            }),
            catchError((error) =>
              of(
                SubmitSupplierPrequalificationActions.failure({
                  error: {
                    message: error?.message || 'Failed to submit supplier prequalification',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
